import { createSelector } from 'reselect';
import { selectUserProfile } from './profile.selector';
import i18n from '../../i18n';
import { EXPENSE_ACTION_TYPES, REPORT_STATUS } from '../../constants/Enums';

export const selectExpenseFeatureState = (state) => state.expense;

export const selectExpenseNumber = createSelector(
  selectExpenseFeatureState,
  (state) => state && state.expenseNumber
);

export const selectExpenseMiliage = createSelector(
  selectExpenseFeatureState,
  (state) => state && state.miliage
);

export const selectExpenseMiliageLastPosition = createSelector(
  selectExpenseFeatureState,
  (state) => state && state.lastPosition
);

export const selectExpenseCategories = createSelector(selectExpenseFeatureState, (state) => {
  if (state && state.expenseCategories) {
    state.expenseCategories.map((categoryGroup) => {
      categoryGroup.GroupNameTranslated = categoryGroup.GroupName.replace(/ /g, '');
      categoryGroup.SubCategories.forEach((subCategory) => {
        subCategory.NameTranslated = subCategory.Name.replace(/ /g, '');
      });
    });
  }
  return state && state.expenseCategories;
});

export const selectCarExpenseCategory = createSelector(selectExpenseCategories, (state) => {
  return null;
});

export const selectExpenseAttendees = createSelector(
  selectExpenseFeatureState,
  (state) => state && state.attendees
);

export const selectCurrencies = createSelector(selectExpenseFeatureState, (state) => {
  return state.currencies;
});

export const selectCreditCards = createSelector(selectExpenseFeatureState, (state) => {
  return state.creditCards;
});

export const selectAutoMileageCategory = createSelector(selectExpenseCategories, (state) => {
  if (!state) {
    return null;
  }

  const autoMileage = state.find((category) => category.Name?.toLowerCase() === 'auto mileage');
  return autoMileage?.CategoryId;
});

export const selectExpenseDistanceRates = createSelector(
  selectExpenseFeatureState,
  (state) => state && state.distanceRates
);

export const selectExpenseDistanceRatesForActionSheet = createSelector(
  selectExpenseDistanceRates,
  (state) => {
    if (!Array.isArray(state)) {
      return null;
    }
    const results = state.map((item) => item.Name.replace(/ /g, ''));
    results.push(i18n.t('Cancel'));
    return results;
  }
);

export const selectExpenseList = createSelector(
  selectExpenseFeatureState,
  (state) => state && state.list
);

export const selectExpensePendingList = createSelector(selectExpenseList, (state) => {
  if (!state) {
    return [];
  }
  try {
    // Filter the state for items with a 'Pending' status, if the state is an array.
    return Array.isArray(state)
      ? state.filter((item) => item?.Status === REPORT_STATUS.PENDING)
      : [];
  } catch (error) {
    // If JSON.parse throws an error (e.g., if `state` is not valid JSON), log the error and return an empty array.
    console.error('Error parsing state:', error);
    return [];
  }
});

export const selectLastExpensePendingReport = createSelector(
  selectExpensePendingList,
  (state) => state && state[state.length - 1]
);

export const selectNewReport = createSelector(
  selectExpenseNumber,
  selectUserProfile,
  (number, profile) => {
    if (number === undefined || number === null || !profile) {
      return null;
    }
    return {
      id: number,
      Name: `${profile.FirstName}'s Expense ${number}`,
    };
  }
);

export const selectActiveReport = createSelector(
  selectLastExpensePendingReport,
  selectNewReport,
  (lastReport, newReport) => (lastReport ? lastReport : newReport)
);

export const selectCurrentReportIdForUpdate = createSelector(
  selectExpenseFeatureState,
  (state) => state.currentReportIdForUpdate
);

export const selectCurrentSelectedSubCategoryForManualTab = createSelector(
  selectExpenseFeatureState,
  (state) => state.currentSelectedSubCategoryForManualTab
);

export const selectReceiptsOfCurrentUser = createSelector(
  selectExpenseFeatureState,
  (state) => state.receiptsOfCurrentUser
);

export const selectReceiptData = createSelector(
  selectExpenseFeatureState,
  (state) => state.receiptData
);

export const selectCurrentExpenseReportCardModalItem = createSelector(
  selectExpenseFeatureState,
  (state) => state.currentExpenseReportCardModalItem
);

export const selectExpenseStates = createSelector(selectExpenseFeatureState, (state) => {
  return {
    [EXPENSE_ACTION_TYPES.CREATION]: state[EXPENSE_ACTION_TYPES.CREATION],
    [EXPENSE_ACTION_TYPES.UPDATE]: state[EXPENSE_ACTION_TYPES.UPDATE],
  };
});

export const selectShouldFetchCharges = createSelector(selectExpenseFeatureState, (state) => {
  return state.shouldFetchCharges;
});

export const selectSelectedChargesQueue = createSelector(
  selectExpenseFeatureState,
  (state) => state.selectedChargesQueue
);
